import { base64UrlDecode, base64UrlEncode } from "./bytes";

export const QR_PROTOCOL_VERSION = 1 as const;
export const SESSION_ID_BYTES = 16;

const SESSION_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/u;

export type InvitationParseResult =
  | {
      ok: true;
      sessionId: string;
      version: 1;
    }
  | {
      ok: false;
      reason:
        | "malformed-route"
        | "missing-session"
        | "invalid-session"
        | "unsupported-version";
      message: string;
    };

export function generateSessionId(): string {
  const bytes = new Uint8Array(SESSION_ID_BYTES);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export function isValidSessionId(value: string): boolean {
  if (!SESSION_ID_PATTERN.test(value)) {
    return false;
  }

  try {
    return base64UrlDecode(value).byteLength === SESSION_ID_BYTES;
  } catch {
    return false;
  }
}

export function buildInvitationUrl(
  sessionId: string,
  baseUrl = globalThis.location?.href ?? "http://localhost/",
): string {
  if (!isValidSessionId(sessionId)) {
    throw new Error("A valid 128-bit session ID is required.");
  }

  const url = new URL(baseUrl);
  url.pathname = "/";
  url.search = "";
  url.hash = `/receive?s=${encodeURIComponent(sessionId)}&v=${QR_PROTOCOL_VERSION.toString()}`;
  return url.toString();
}

function getHashRoute(value: string): string | undefined {
  if (value.startsWith("#")) {
    return value.slice(1);
  }
  if (value.startsWith("/receive")) {
    return value;
  }

  try {
    const url = new URL(value, "http://localhost/");
    return url.hash.startsWith("#") ? url.hash.slice(1) : undefined;
  } catch {
    return undefined;
  }
}

export function parseInvitationRoute(value: string): InvitationParseResult {
  const route = getHashRoute(value);
  if (route === undefined) {
    return {
      ok: false,
      reason: "malformed-route",
      message: "This invitation route is not valid.",
    };
  }

  const queryIndex = route.indexOf("?");
  const path = queryIndex >= 0 ? route.slice(0, queryIndex) : route;
  if (path !== "/receive") {
    return {
      ok: false,
      reason: "malformed-route",
      message: "This invitation does not open the receiver.",
    };
  }

  const params = new URLSearchParams(
    queryIndex >= 0 ? route.slice(queryIndex + 1) : "",
  );
  const sessionId = params.get("s");
  const version = params.get("v");
  if (sessionId === null) {
    return {
      ok: false,
      reason: "missing-session",
      message: "This invitation is missing its session ID.",
    };
  }
  if (!isValidSessionId(sessionId)) {
    return {
      ok: false,
      reason: "invalid-session",
      message: "This invitation has an invalid session ID.",
    };
  }
  if (version !== QR_PROTOCOL_VERSION.toString()) {
    return {
      ok: false,
      reason: "unsupported-version",
      message: "This invitation uses an unsupported QR protocol version.",
    };
  }
  if (params.getAll("s").length !== 1 || params.getAll("v").length !== 1) {
    return {
      ok: false,
      reason: "malformed-route",
      message: "This invitation contains duplicate parameters.",
    };
  }
  if ([...params.keys()].some((key) => key !== "s" && key !== "v")) {
    return {
      ok: false,
      reason: "malformed-route",
      message: "This invitation contains unsupported parameters.",
    };
  }

  return { ok: true, sessionId, version: QR_PROTOCOL_VERSION };
}

export function shortSessionCode(sessionId: string): string {
  if (!isValidSessionId(sessionId)) {
    return "INVALID";
  }
  return `${sessionId.slice(0, 4)}-${sessionId.slice(-4)}`.toUpperCase();
}
